import React, { forwardRef, useImperativeHandle, useRef } from "react";
import { RCCButton, RCCGrid, RCCInput, RCCSwitch, RCCTooltip } from "react-common-components";
import CardContractItemType from "src/components/CardContractItemType";
import LovHomePage from "src/components/Lov/HomePage";
import { IRefSchedule } from "src/components/ScheduleRestriction/interface";
import Tabs from "src/components/Tabs";
import { useAuth } from "src/context/Auth/context";
import { useTranslation } from "src/context/Translations/context";
import { v4 } from "uuid";
import { defaultStateSchedule } from "../../interface";
import { validatePassword } from "../../schemas";
import CompaniesUser from "../CompaniesUser";
import GroupsUser from "../GroupsUser";
import Restrictions from "../Restrictions";
import { IComponentRegisterUser, IRefRegisterUser } from "./interface";


const RegisterUser: React.ForwardRefRenderFunction<IRefRegisterUser, IComponentRegisterUser> = ({
	control,
	error,
	setValue,
	register,
	clearErrors,
	businessGroupId
}, ref) => {

	const { translate } = useTranslation();
	const { businessGroup } = useAuth();

	const refSchedule = useRef<IRefSchedule>(null);

	const generatePassword = async () => {	
		try{
			let passwordGenerated = v4();
			// vowels to uppercase and consonants to lowercase
			passwordGenerated = passwordGenerated.split("").reduce((a, c) => a + (/[aeiou]/i.test(c) ? c.toUpperCase() : c.toLowerCase()), "");
			passwordGenerated = passwordGenerated.slice(0,10);
			await validatePassword(translate).validate(passwordGenerated, 
				{abortEarly: false}
			);
			setValue("password", passwordGenerated);
		}catch(e){
			generatePassword();
		}
	};

	useImperativeHandle(ref,() => ({
		getScheduleData: () => refSchedule.current?.getInnerData() ?? []
	}));

	return (
		<Tabs 
			name="tabUserMaintenance"
			tabInit={0}
			options={[{
				label: translate("Dados Gerais"),
				content: <>
					{businessGroup.efa001Id === businessGroupId && (
						<RCCGrid container item>
							<CardContractItemType 
								type="13"
							/>
						</RCCGrid>
					)}
					<RCCGrid
						container
						item
						direction="column"
						alignItems="center"
					>
						<RCCGrid
							container
							item
							xs={12}
						>
							<RCCInput 
								register={register}
								name="efa005Nome"
								label={translate("Nome")}
								maxLength={60}
								error={error.efa005Nome}
								autoComplete="off"
								required
							/>
						</RCCGrid>
						<RCCGrid
							container
							item
							xs={12}
						>
							<RCCInput 
								register={register}
								name="efa005Email"
								label={translate("Email")}
								maxLength={200}
								error={error.efa005Email}
								required
							/>
						</RCCGrid>
						<RCCGrid
							container
							item
							wrap="nowrap"
							alignItems="baseline"
							xs={12}
							spacing={1}
						>
							<RCCInput 
								name="password"
								register={register}
								label={translate("Senha")}
								maxLength={30}
								error={error.password}
								autoComplete="off"
								required
							/>
							<RCCTooltip
								color="purple"
								text={translate("Gerar senha aleatória")}
							>
								<RCCButton
									icon={{name: "key", color: "#FFF"}}
									color="purple"
									layout="isRounded"
									onClick={generatePassword}
									size="sm"
								/>
							</RCCTooltip>
						</RCCGrid>
						<RCCGrid
							container
							item
							xs={12}
						>
							<RCCSwitch 
								name="changePassword"
								register={register}
								label={translate("Alterar senha no próximo acesso")}
								defaultChecked	
							/>
						</RCCGrid>
						<RCCGrid
							container
							item
							xs={12}
						>
							<RCCInput 
								register={register}
								name="efa005Tratamento"
								label={translate("Tratamento")}
								maxLength={40}
								error={error.efa005Tratamento}
							/>
						</RCCGrid>
						<RCCGrid
							container
							item
							xs={12}
						>
							<RCCInput 
								register={register}
								name="daysExpirePassword"
								label={translate("Dias para expirar a senha")}
								maxLength={5}
								error={error.daysExpirePassword}
								type="number"
								min={0}
							/>
						</RCCGrid>
						<RCCGrid
							container
							item
							xs={12}
						>
							<LovHomePage 
								control={control}
								name="efa005Paginicial"
								label={translate("Página Inicial")}
								maxLength={40}
								error={error.efa005Paginicial}
								isClearable
							/>
						</RCCGrid>
					</RCCGrid>
				</>
			},{
				label: translate("Grupo de usuários"),
				content: <GroupsUser 
					clearErrors={clearErrors}
					control={control}
					defaultValue={[]}
					error={error}
				/>
			},
			{
				label: translate("Empresas"),
				content: <CompaniesUser
					clearErrors={clearErrors}
					control={control}
					defaultValue={[]}
					error={error}
				/>
			},
			{
				label: translate("Restrições"),
				content: <Restrictions 
					register={register} 
					refSchedule={refSchedule} 
					data={{
						schedule: defaultStateSchedule,
						validateIp: false
					}}  
				/>
			}
			]}
		/>
	);
};

export default forwardRef(RegisterUser);
